import { z } from "zod";
import { SupportedCurrencyCodesSchema } from "./currencies.js";
/**
 * A settlement records a direct payment from one participant to another to
 * pay down what they owe — "A paid B 20.00". Unlike an expense it has no
 * items or splits: it is a single transfer of `amount` (group-currency minor
 * units) from `payerId` to `payeeId`.
 *
 * Effect on balances (net = paid − owed, positive when owed): the payer's net
 * moves up by `amount` (they discharged a debt) and the payee's net moves down
 * by `amount` (they received what they were owed), so the group's balances
 * still sum to zero. See `computeParticipantNetBalance` in the service layer,
 * which folds settlements in alongside expenses.
 */
export const SettlementSchema = z.strictObject({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    /** Participant who made the payment (their debt goes down). */
    payerId: z.string().uuid(),
    /** Participant who received the payment (what they were owed goes down). */
    payeeId: z.string().uuid(),
    /**
     * Amount transferred, in the group currency's smallest unit (cents, pence).
     * Always positive. Settlements are always denominated in the group currency,
     * so — unlike expenses — there is no separate `charge`/`groupCharge` split.
     */
    amount: z.number().int().positive(),
    /** Denomination of `amount`. Always equals the group's currency. */
    currency: SupportedCurrencyCodesSchema,
    /** Optional free-text note ("Venmo", "cash", ...). */
    note: z.string().max(120).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * ParticipantId of the caller who recorded the settlement — the trusted
     * basis for the MEMBER edit-own/delete-own authorization check, mirroring
     * `Expense.createdBy`. Optional for forward-compatibility with any rows
     * written before it was persisted.
     */
    createdBy: z.string().uuid().optional(),
    /**
     * Optimistic-concurrency token, incremented on every successful update.
     * Returned on reads so clients can echo it back as the required `version`.
     */
    version: z.number().int().positive(),
});
/**
 * Create payload. `currency` is intentionally omitted: a settlement is always
 * booked in the group currency, which the server reads from the group head.
 * `payerId` and `payeeId` must differ and both must be participants of the
 * group — enforced server-side.
 */
export const CreateSettlementRequestSchema = z.strictObject({
    payerId: z.string().uuid(),
    payeeId: z.string().uuid(),
    amount: z.number().int().positive(),
    note: z.string().max(120).optional(),
});
export const UpdateSettlementRequestSchema = z.strictObject({
    payerId: z.string().uuid().optional(),
    payeeId: z.string().uuid().optional(),
    amount: z.number().int().positive().optional(),
    note: z.string().max(120).optional(),
    /**
     * Last-known version, for optimistic concurrency. Required: every update is
     * conditioned on it so a stale client cannot clobber a concurrent edit. A
     * stale value yields 409.
     */
    version: z.number().int().positive(),
});
/**
 * One entry in the "who owes who, simplified" breakdown: `from` should pay
 * `to` the given `amount` (group-currency minor units) to move the group
 * toward settled. The set is the minimal transfer plan produced by the
 * greedy debt-simplification over every participant's net balance.
 */
export const SettlementSuggestionSchema = z.strictObject({
    from: z.string().uuid(),
    to: z.string().uuid(),
    amount: z.number().int().positive(),
});
/**
 * Response of `GET /groups/{groupId}/balances`: each participant's net balance
 * plus the simplified transfer plan to settle the group. `net` is positive
 * when the participant is owed, negative when they owe (group-currency minor
 * units). Participants with a zero net are included so clients can render a
 * complete roster.
 */
export const GroupBalancesSchema = z.strictObject({
    currency: SupportedCurrencyCodesSchema,
    balances: z.array(z.strictObject({
        participantId: z.string().uuid(),
        net: z.number().int(),
    })),
    suggestions: z.array(SettlementSuggestionSchema),
});
